/**
 * Distance-tapered yield and passenger willingness-to-pay.
 *
 * Yield per RPK falls exponentially with stage length, so short sectors earn far
 * more per km than long-haul ones and can cover their fixed departure costs:
 *
 *   Yield(d) = Ybase * (1 + ALPHA * e^(-BETA * d))
 *
 * Willingness-to-pay (WTP) is modelled per segment. Business travellers pay for
 * frequency and peak-hour departures and barely react to price; leisure
 * travellers are price-elastic, more so on short sectors that have rail and
 * road substitutes, and pay more on weekends. Pure functions only.
 */

/** Baseline long-haul yield, USD per revenue passenger kilometre. */
export const BASE_YIELD_USD_PER_RPK = 0.09;
/**
 * Short-haul yield multiplier at zero distance, in addition to the base. Calibrated at 4 (the brief's ~2.5 left a
 * 283 km sector at about $0.22 per RPK, too thin to cover a turboprop's departure costs); it leaves long-haul
 * fares unchanged and lifts a 1,000 km fare by about 15%.
 */
export const SHORT_HAUL_YIELD_ALPHA = 4;
/** Tapering decay rate per km. */
export const YIELD_DECAY_BETA = 0.002;
/** Lowest economy fare any sector can be priced at, whatever its distance. */
export const MIN_ECONOMY_FARE_USD = 39;

/** Business peaks: 07:00-09:00 and 17:00-19:00 local, as [start, end) hours. */
export const BUSINESS_PEAK_WINDOWS: readonly (readonly [number, number])[] = [
  [7, 9],
  [17, 19],
];

/** Below this stage length business fares carry the full short-haul peak premium. */
const SHORT_HAUL_BUSINESS_KM = 500;
/** Business-to-economy fare multiple on a long sector, mirrors the reference fare table. */
const BUSINESS_BASE_MULTIPLE_MIN = 2.5;
const BUSINESS_BASE_MULTIPLE_LONG_EXTRA = 2.0;
const BUSINESS_BASE_MULTIPLE_FULL_KM = 6000;
/** Extra business multiple at a peak departure on a very short sector: 2.5x + 3.0x = 5.5x. */
const BUSINESS_PEAK_SHORT_HAUL_EXTRA = 3.0;
/** Peak-hour premium shrinks to this share outside the peak windows. */
const BUSINESS_OFF_PEAK_PREMIUM_SHARE = 0.15;
/** Departures per day at which business frequency appeal saturates. */
const BUSINESS_FREQUENCY_SATURATION = 8;
const BUSINESS_FREQUENCY_FLOOR = 0.85;
const BUSINESS_FREQUENCY_CEILING = 1.1;
/** Hard ceiling on the business fare multiple over economy. */
const BUSINESS_MAX_MULTIPLE = 6;

const LEISURE_ELASTICITY_LONG_HAUL = 1.0;
const LEISURE_ELASTICITY_SHORT_HAUL_EXTRA = 0.6;
const LEISURE_ELASTICITY_DECAY_KM = 800;
const BUSINESS_ELASTICITY = 0.35;

/** Leisure fare index by day of week, 0 = Sunday. Friday and Sunday spike. */
const LEISURE_DAY_OF_WEEK_INDEX: readonly number[] = [1.12, 0.92, 0.9, 0.92, 0.98, 1.14, 1.06];

/* -------------------------------------------------------------------------- *
 * Departure-slot business yield
 * -------------------------------------------------------------------------- */

/** Business share of a market above which slot pricing applies. */
export const SLOT_BUSINESS_SPLIT_THRESHOLD = 0.3;
/** Business share at which the slot multiplier reaches its far end. */
const SLOT_BUSINESS_SPLIT_FULL = 0.7;
const MORNING_PEAK_RANGE: readonly [number, number] = [1.5, 2.0];
const EVENING_PEAK_RANGE: readonly [number, number] = [1.4, 1.8];
const MIDDAY_RANGE: readonly [number, number] = [1.0, 0.85];

export type DepartureSlot = 'MORNING_PEAK' | 'EVENING_PEAK' | 'MIDDAY_OFF_PEAK' | 'SHOULDER';

/** Slot of a local departure hour (decimal hours): 07-09, 17-19 and 11-14 are the priced slots. */
export function departureSlot(localHour: number): DepartureSlot {
  const hour = ((localHour % 24) + 24) % 24;
  if (hour >= 7 && hour < 9) return 'MORNING_PEAK';
  if (hour >= 17 && hour < 19) return 'EVENING_PEAK';
  if (hour >= 11 && hour < 14) return 'MIDDAY_OFF_PEAK';
  return 'SHOULDER';
}

/** Local decimal hour for a UTC minute of the sim and a UTC offset. */
export function localHourOf(utcMinute: number, utcOffsetHours: number): number {
  const hours = utcMinute / 60 + utcOffsetHours;
  return ((hours % 24) + 24) % 24;
}

/**
 * Business and premium-economy fare willingness-to-pay multiplier for a departure slot: 1.5x-2.0x in the
 * morning peak, 1.4x-1.8x in the evening peak, 1.0x-0.85x mid-day. Position in each range follows how
 * business-heavy the market is, from 30% (no uplift) to 70% (full). Below a 30% business share every slot is 1.
 */
export function rawSlotMultiplier(localHour: number, businessShare: number): number {
  if (businessShare <= SLOT_BUSINESS_SPLIT_THRESHOLD) return 1;
  const intensity = clamp(
    (businessShare - SLOT_BUSINESS_SPLIT_THRESHOLD) / (SLOT_BUSINESS_SPLIT_FULL - SLOT_BUSINESS_SPLIT_THRESHOLD),
    0,
    1,
  );
  const lerp = (range: readonly [number, number]): number => range[0] + (range[1] - range[0]) * intensity;
  switch (departureSlot(localHour)) {
    case 'MORNING_PEAK':
      return lerp(MORNING_PEAK_RANGE);
    case 'EVENING_PEAK':
      return lerp(EVENING_PEAK_RANGE);
    case 'MIDDAY_OFF_PEAK':
      return lerp(MIDDAY_RANGE);
    case 'SHOULDER':
      return 1;
  }
}

/**
 * The multiplier on booked Business and Premium Economy revenue. A higher fare turns away some price-sensitive
 * business travellers, so the willingness-to-pay multiplier m earns m^(1 - elasticity), not m.
 */
export function slotYieldMultiplier(localHour: number, businessShare: number): number {
  return Math.pow(rawSlotMultiplier(localHour, businessShare), 1 - BUSINESS_ELASTICITY);
}

export type TravelSegment = 'BUSINESS' | 'LEISURE';

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Yield per RPK at a stage length. Equal to `BASE_YIELD_USD_PER_RPK * (1 + 2.5)` at d = 0 and tends to the base on long-haul. */
export function yieldPerRpk(distanceKm: number): number {
  const d = Math.max(0, distanceKm);
  return BASE_YIELD_USD_PER_RPK * (1 + SHORT_HAUL_YIELD_ALPHA * Math.exp(-YIELD_DECAY_BETA * d));
}

/** Tapered one-way economy fare for a stage length, before any wealth adjustment. */
export function taperedEconomyFareUSD(distanceKm: number): number {
  return Math.max(MIN_ECONOMY_FARE_USD, yieldPerRpk(distanceKm) * Math.max(0, distanceKm));
}

/** True when a local departure hour falls in a business peak window. */
export function isBusinessPeakHour(localHour: number): boolean {
  const hour = ((localHour % 24) + 24) % 24;
  return BUSINESS_PEAK_WINDOWS.some(([start, end]) => hour >= start && hour < end);
}

/** Business fare multiple over economy on a long sector, before peak and frequency effects. */
export function baseBusinessMultiple(distanceKm: number): number {
  return (
    BUSINESS_BASE_MULTIPLE_MIN +
    BUSINESS_BASE_MULTIPLE_LONG_EXTRA * Math.min(1, Math.max(0, distanceKm) / BUSINESS_BASE_MULTIPLE_FULL_KM)
  );
}

/** Appeal of a schedule to business travellers: 0.85 for a single daily flight up to 1.1 at 8+ flights. */
export function businessFrequencyFactor(departuresPerDay: number): number {
  const reach = clamp(departuresPerDay / BUSINESS_FREQUENCY_SATURATION, 0, 1);
  return BUSINESS_FREQUENCY_FLOOR + (BUSINESS_FREQUENCY_CEILING - BUSINESS_FREQUENCY_FLOOR) * reach;
}

export interface BusinessWtpInput {
  readonly distanceKm: number;
  /** Departures per day the airline offers on the pair. */
  readonly departuresPerDay: number;
  /** Local departure hour, 0-23. */
  readonly localHour: number;
}

/**
 * Business fare as a multiple of the economy fare. On sectors under 500 km a
 * peak-hour departure reaches 4x-6x economy; off-peak it falls back toward the
 * ordinary business multiple. Frequency lifts or trims the result.
 */
export function businessFareMultiple(input: BusinessWtpInput): number {
  const base = baseBusinessMultiple(input.distanceKm);
  const shortHaul = clamp(1 - input.distanceKm / SHORT_HAUL_BUSINESS_KM, 0, 1);
  const peakShare = isBusinessPeakHour(input.localHour) ? 1 : BUSINESS_OFF_PEAK_PREMIUM_SHARE;
  const peakExtra = BUSINESS_PEAK_SHORT_HAUL_EXTRA * shortHaul * peakShare;
  // A 100 km peak departure with 8+ daily flights lands near 5.5x; one flight a day trims it to about 4.2x.
  return clamp((base + peakExtra) * businessFrequencyFactor(input.departuresPerDay), 1, BUSINESS_MAX_MULTIPLE);
}

/** Share of business demand that books peak-hour departures; sets the all-day average business fare. */
const BUSINESS_PEAK_DEMAND_SHARE = 0.45;
/** Daily frequency a reference market is assumed to offer when no timetable is known. */
const REFERENCE_DEPARTURES_PER_DAY = 4;

/**
 * All-day average business fare multiple for a market with no timetable: the peak and off-peak multiples
 * blended by the share of business demand that books peak departures.
 */
export function referenceBusinessMultiple(distanceKm: number): number {
  const at = (localHour: number): number =>
    businessFareMultiple({ distanceKm, departuresPerDay: REFERENCE_DEPARTURES_PER_DAY, localHour });
  return BUSINESS_PEAK_DEMAND_SHARE * at(8) + (1 - BUSINESS_PEAK_DEMAND_SHARE) * at(13);
}

/** Leisure price elasticity: about 1.6 on very short sectors, tending to 1.0 on long-haul. */
export function leisureElasticity(distanceKm: number): number {
  return (
    LEISURE_ELASTICITY_LONG_HAUL +
    LEISURE_ELASTICITY_SHORT_HAUL_EXTRA * Math.exp(-Math.max(0, distanceKm) / LEISURE_ELASTICITY_DECAY_KM)
  );
}

export function businessElasticity(): number {
  return BUSINESS_ELASTICITY;
}

/** Leisure fare index for a weekday, 0 = Sunday. */
export function leisureWeekdayIndex(dayOfWeek: number): number {
  const index = ((Math.floor(dayOfWeek) % 7) + 7) % 7;
  return LEISURE_DAY_OF_WEEK_INDEX[index] ?? 1;
}

export interface FareQuoteInput {
  readonly distanceKm: number;
  readonly departuresPerDay: number;
  readonly localHour: number;
  /** 0 = Sunday. */
  readonly dayOfWeek: number;
  /** Market wealth adjustment on the economy fare, 1 = reference market. */
  readonly wealthFactor?: number;
}

export interface FareQuote {
  readonly leisureUSD: number;
  readonly businessUSD: number;
  readonly businessMultiple: number;
  readonly yieldPerRpkUSD: number;
}

/** Willingness-to-pay fares for both segments on one departure. */
export function quoteSegmentFares(input: FareQuoteInput): FareQuote {
  const wealth = input.wealthFactor ?? 1;
  const economy = taperedEconomyFareUSD(input.distanceKm) * wealth;
  const multiple = businessFareMultiple(input);
  return {
    leisureUSD: Math.round(economy * leisureWeekdayIndex(input.dayOfWeek)),
    businessUSD: Math.round(economy * multiple),
    businessMultiple: multiple,
    yieldPerRpkUSD: yieldPerRpk(input.distanceKm),
  };
}
